"use client";
import React, { useEffect } from "react";
import Link from "@/components/Link";
import { InstantSearch, useInfiniteHits } from "react-instantsearch";
import findIndex from "lodash/findIndex";

import { Container } from "@/components/Blog/Layout";
import NavigationButton from "@/components/Blog/BlogNavigation/NavigationButton";
import { BLOG_INDEX_URL } from "@/data/helpers/blog/urls";
import { blogSearchClient } from "@/components/Blog/BlogSearch/client";
import { BLOG_SEARCH_INDEX_NAME } from "@/components/Blog/BlogSearch/utils";

type Props = {
  uid: string;
};

const makeUrl = (uid: string) => `${BLOG_INDEX_URL}/${uid}`;

const PostNavigationControls: React.FC<Props> = ({ uid }) => {
  const { hits, isLastPage, showMore } = useInfiniteHits();

  const hitsExists = hits?.length;
  const position = findIndex(hits, (hit) => hit.uid === uid);
  const positionFound = position > -1;
  const isLast = positionFound && position === hits.length - 1;
  const prevUid = position > 0 ? (hits[position - 1].uid as string) : undefined;
  const nextUid =
    positionFound && !isLast ? (hits[position + 1].uid as string) : undefined;

  // load page if post not on this chunk
  useEffect(() => {
    if (hitsExists && position === -1 && showMore) {
      showMore && showMore();
    }
  }, [position, showMore, hitsExists]);

  // load if item is last in this chunk
  useEffect(() => {
    if (positionFound && !isLastPage && !nextUid && showMore) {
      showMore();
    }
  }, [positionFound, isLastPage, nextUid, showMore]);

  return (
    <Container className="grid grid-cols-2">
      <div>
        {prevUid && (
          <Link href={makeUrl(prevUid as string)}>
            <NavigationButton>Newer Posts</NavigationButton>
          </Link>
        )}
      </div>
      <div className="text-right">
        {nextUid && (
          <Link href={makeUrl(nextUid as string)}>
            <NavigationButton>Older Posts</NavigationButton>
          </Link>
        )}
      </div>
    </Container>
  );
};

const PostNavigation: React.FC<Props> = ({ uid }) => (
  <InstantSearch
    searchClient={blogSearchClient}
    indexName={BLOG_SEARCH_INDEX_NAME}
  >
    <PostNavigationControls uid={uid} />
  </InstantSearch>
);

export default PostNavigation;
